/**
 * Types lecture (DB rows) — Zod output dérivés des schémas d'écriture +
 * extensions pour les colonnes système (id, tenant_id, status, timestamps…).
 *
 * Pourquoi ?
 * ----------
 * Avant 8.B : chaque page web réécrivait à la main son type `Company`,
 * `Contact`, etc. depuis le SELECT du backend. Trois sources de vérité :
 *   1. SQL (les colonnes sélectionnées dans les routes Hono)
 *   2. Zod schema d'écriture (CreateCompanySchema, etc.)
 *   3. Type web recopié manuellement
 *
 * Conséquence : à chaque ajout de colonne, on touche 3 endroits, et le typage
 * web peut diverger silencieusement du backend (ex. nullable oublié → bug en
 * runtime). C'est exactement la dette qu'on refuse en v1.
 *
 * Stratégie 8.B : on publie ici un type lecture par entité métier qui :
 *   - reprend les champs du Create schema en `.partial()` (tout est nullable
 *     côté DB sauf le minimum requis),
 *   - ajoute les colonnes système (id, tenant_id, status, timestamps).
 *
 * Le web consomme ces types via `import type { ProjectRow } from
 * "@kollectivia/shared"`. Plus de duplication.
 *
 * IMPORTANT — ces types décrivent ce que l'API RENVOIE (donc valeurs déjà
 * sérialisées : dates en ISO string, pas Date object). Ils ne servent PAS à
 * valider le runtime côté front (le front fait confiance au backend qui a
 * déjà validé) — uniquement pour le typage.
 *
 * Quand changer ?
 * ---------------
 * Quand on ajoute une colonne à app.companies / app.projects / app.contacts
 * ET qu'elle est sélectionnée par le SELECT du backend, on l'ajoute ici
 * aussi. Si on l'ajoute SEULEMENT dans le SELECT mais pas ici, le typage
 * web ne la verra pas (pas grave) ; si on la promet ici sans qu'elle soit
 * dans le SELECT, le web crashera à runtime — c'est le seul mode d'échec.
 *
 * Pas tenant-specific : ces types décrivent le schéma DB universel, pas les
 * formulaires d'intake (qui peuvent être custom par tenant en v1+).
 */

import { z } from "zod";

import { PROJECT_STATUSES } from "../constants.js";

import { CreateCompanySchema, CreateContactSchema } from "./companies.js";
import { CreateProjectSchema } from "./projects.js";

// =============================================================================
// CompanyRow — `app.companies` SELECT côté GET /v1/companies
// =============================================================================

export const CompanyRowSchema = CreateCompanySchema.partial().extend({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),

  // Required par construction (NOT NULL en DB) — re-affirmer après .partial().
  legal_name: z.string(),
  language: z.enum(["fr", "en"]),
  tags: z.array(z.string()),

  // Champs métier qui restent nullable en DB.
  trade_name: z.string().nullable(),
  business_number: z.string().nullable(),
  /**
   * Type d'identifiant entreprise (Chunk D-NEW-D Phase 1, migration 0031).
   * NULL = legacy non spécifié (rows pre-migration). Validation enum stricte
   * Zod-only (pas de CHECK constraint DB-level — décision PO Q3 = A).
   */
  identification_type: z.string().nullable(),
  industry: z.string().nullable(),
  founded_year: z.number().int().nullable(),
  address_street: z.string().nullable(),
  address_city: z.string().nullable(),
  address_province: z.string().nullable(),
  address_postal: z.string().nullable(),
  address_country: z.string().nullable(),
  phone: z.string().nullable(),
  fax: z.string().nullable(),
  email: z.string().nullable(),
  website: z.string().nullable(),
  plan_admin_name: z.string().nullable(),
  plan_admin_email: z.string().nullable(),
  employee_count: z.number().int().nullable(),
  has_seasonal: z.boolean().nullable(),
  has_commission: z.boolean().nullable(),
  has_unionized: z.boolean().nullable(),
  has_remote_abroad: z.boolean().nullable(),
  considers_remote_abroad: z.boolean().nullable(),
  family_ties: z.boolean().nullable(),
  family_ties_note: z.string().nullable(),
  has_current_plan: z.boolean().nullable(),
  current_insurer: z.string().nullable(),
  current_policy_number: z.string().nullable(),
  current_since_date: z.string().nullable(),
  current_renewal_date: z.string().nullable(),
  primary_broker_id: z.string().uuid().nullable(),
  notes: z.string().nullable(),

  // D43 — friendly names + preferences (migration 0022)
  display_name: z.string().nullable(),
  display_name_locked_at: z.string().nullable(),
  preferred_language: z.enum(["fr", "en"]).nullable(),

  // Système.
  status: z.enum(["active", "archived"]),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.string().uuid().nullable(),
});
export type CompanyRow = z.infer<typeof CompanyRowSchema>;

// =============================================================================
// ContactRow — `app.contacts` SELECT côté GET /v1/companies/:id/contacts
// =============================================================================

export const ContactRowSchema = CreateContactSchema.partial().extend({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  company_id: z.string().uuid(),

  // NOT NULL en DB.
  full_name: z.string(),
  is_primary: z.boolean(),

  // Nullable.
  title: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  notes: z.string().nullable(),

  // D43 — preferences (migration 0022)
  preferred_language: z.enum(["fr", "en"]).nullable(),
  preferred_channel: z.enum(["email", "phone", "sms"]).nullable(),

  // Système.
  created_at: z.string(),
  updated_at: z.string(),
});
export type ContactRow = z.infer<typeof ContactRowSchema>;

// =============================================================================
// ProjectRow — `app.projects` SELECT côté GET /v1/projects
// =============================================================================
// Note : on ne dérive PAS de CreateProjectSchema directement parce que les
// défauts (insurer_codes.min(1), guarantee_codes default([])) ne sont pas
// pertinents pour la lecture. On redéfinit explicitement.

export const ProjectRowSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  company_id: z.string().uuid(),

  broker_id: z.string().uuid().nullable(),
  contact_id: z.string().uuid().nullable(),

  reference: z.string().nullable(),
  /**
   * Code système stable PRJ-YYYY-NNNNNN auto-généré par tenant+année.
   * Migration 0023 — NOT NULL après backfill, immutable, format strict.
   * Distinct de `reference` (texte libre legacy).
   */
  reference_code: z.string().regex(/^PRJ-\d{4}-\d{6}$/),
  title: z.string(),

  status: z.enum(PROJECT_STATUSES),
  stage_label: z.string().nullable(),
  progress: z.number().int().nullable(),

  effect_date: z.string().nullable(),
  renewal_target_date: z.string().nullable(),
  deadline_date: z.string().nullable(),

  insurer_codes: z.array(z.string()),
  guarantee_codes: z.array(z.string()),

  requested_commission_rate: z.number().nullable().optional(),
  notes: z.string().nullable(),
  tags: z.array(z.string()),

  // D43 — preferences (migration 0022)
  preferred_language: z.enum(["fr", "en"]).nullable(),

  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.string().uuid().nullable(),

  // 9.T.5.v3.6 QA1 — subselect scalaire depuis app.companies.legal_name via
  // PROJECT_COLS. Non-breaking : champ additif optionnel. Null si company
  // supprimée (rare) OU pour consumers legacy pré-v3.6 qui ne projettent
  // pas ce champ (défensif).
  company_legal_name: z.string().nullable().optional(),
});
export type ProjectRow = z.infer<typeof ProjectRowSchema>;

/**
 * Les états opérationnels d'un dossier — 9.BG.1.
 *
 * **Dérivés, jamais stockés.** Ils se lisent des demandes, des destinataires,
 * des réponses et des échéances, et se recalculent à chaque lecture : ils ne
 * peuvent donc pas se périmer. `projects.status` reste ce qu'il était — la
 * colonne d'archivage et un filtre secondaire — mais il ne décrit pas le
 * travail : sur ses huit valeurs, une seule est écrite par le code.
 *
 * L'ordre dit ce que le conseiller doit faire, pas ce que l'objet est. Un
 * dossier portant à la fois un brouillon et une demande partie attend d'abord
 * les assureurs.
 */
export const PROJECT_OPERATIONAL_STATES = [
  /** Aucune demande vivante : il n'y a pas encore de travail engagé. */
  "to_prepare",
  /** Un brouillon existe, rien n'est parti. */
  "preparing",
  /** Une demande est partie, personne n'a encore répondu. */
  "awaiting_insurers",
  /** Certains ont répondu, d'autres sont attendus. */
  "partial_responses",
  /** Plus personne n'est attendu, et il y a de quoi comparer. */
  "ready_to_compare",
] as const;
export const ProjectOperationalStateSchema = z.enum(PROJECT_OPERATIONAL_STATES);
export type ProjectOperationalState = (typeof PROJECT_OPERATIONAL_STATES)[number];

/**
 * ProjectListItem — ce que renvoie `GET /v1/projects`.
 *
 * Une ligne dit **ce qu'elle attend**, pas ce qu'elle est. Les champs viennent
 * de `app.project_operational` (migration 0082), en une jointure latérale : un
 * aller-retour pour la page, là où interroger `/completeness` par ligne aurait
 * coûté cinq requêtes par dossier.
 *
 * Optionnels : le détail (`GET /:id`) ne les projette pas.
 */
export const ProjectListItemSchema = ProjectRowSchema.extend({
  activity_scope: z.enum(["history", "current"]).optional(),
  recipients_sent_current: z.number().int().nonnegative().optional(),
  recipients_sent_historical: z.number().int().nonnegative().optional(),
  operational_state: ProjectOperationalStateSchema.optional(),
  insurers_solicited: z.number().int().nonnegative().optional(),
  recipients_prepared: z.number().int().nonnegative().optional(),
  recipients_sent: z.number().int().nonnegative().optional(),
  responses_received: z.number().int().nonnegative().optional(),
  responses_awaiting: z.number().int().nonnegative().optional(),
  insurers_declined: z.number().int().nonnegative().optional(),
  requests_draft: z.number().int().nonnegative().optional(),
  requests_sent: z.number().int().nonnegative().optional(),
  next_due_at: z.string().nullable().optional(),
  is_late: z.boolean().nullable().optional(),
});
export type ProjectListItem = z.infer<typeof ProjectListItemSchema>;

/**
 * ProjectRowDetail — ce que renvoie GET /v1/projects/:id (avec submissions_count).
 */
export const ProjectRowDetailSchema = ProjectListItemSchema.extend({
  submissions_count: z.number().int(),
});
export type ProjectRowDetail = z.infer<typeof ProjectRowDetailSchema>;

// =============================================================================
// BrokerRow — `app.users` SELECT côté GET /v1/brokers (sans broker_profile)
// =============================================================================

export const BrokerRowSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  email: z.string(),
  role: z.enum(["broker", "tenant_admin", "super_admin", "client_contact"]),
  full_name: z.string().nullable(),
  locale: z.enum(["fr", "en"]),
  avatar_url: z.string().nullable(),
  totp_enabled: z.boolean().nullable(),
  sms_2fa_enabled: z.boolean().nullable(),
  status: z.string(),
  last_login_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type BrokerRow = z.infer<typeof BrokerRowSchema>;

// =============================================================================
// PaginatedResult<T> — wrapper liste API
// =============================================================================
// Note : déjà typé dans `types.ts` mais répété ici pour ne pas créer de
// dépendance circulaire schemas → types.

export type DbPage<T> = {
  items: T[];
  next_cursor: string | null;
};
